import { Box } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import useInterval from 'beautiful-react-hooks/useInterval';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import awsConfig from '../aws-exports';
import LoadingBackdrop from '../Common/LoadingBackdrop';
import EmptyLayout from '../Components/EmptyLayout';
import { useUpdatePushSubscriptionMutation } from '../generated/core-api/graphql-react-query';
import subscribeToPushNotification from '../notifications/subscribeToPushNotification';

Amplify.configure(awsConfig);

export function useCognitoUser() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userQuery = useQuery(['cognito-user'], () => fetchAuthSession());

  useInterval(() => userQuery.refetch(), 15 * 60 * 1000);
  React.useEffect(() => {
    Hub.listen(
      'auth',
      ({ payload }) => {
        const { event, data } = payload as { event: string, data?: any };
        if (event === 'autoSignIn') {
          queryClient.setQueryData(['cognito-user'], data);
        }
        if (event === 'autoSignIn_failure') {
          navigate('/login', { state: { from: location.pathname } });
        }
        return null;
      },
    );
  }, [location.pathname]);
  return userQuery;
}

export function useRedirectOnSignIn() {
  const navigate = useNavigate();
  const user = useCognitoUser();
  const location = useLocation();
  const { from: redirectTo } = location.state as ({ from: string } | undefined) || { from: '' };

  React.useEffect(() => {
    if (user.isSuccess && user.data) {
      navigate(redirectTo);
    }
  }, [user.isSuccess, user.data, redirectTo]);
}

export default function Authenticator(props: { children: React.ReactNode }) {
  const { children } = props;
  const location = useLocation();
  const user = useCognitoUser();
  const mutation = useUpdatePushSubscriptionMutation();
  const { i18n } = useTranslation();
  React.useEffect(() => {
    if (user.isSuccess && user.data) {
      subscribeToPushNotification().then((res) => {
        if (!res) return;
        mutation.mutate({
          input: {
            pushSubscription: JSON.stringify(res),
            locale: i18n.language,
          },
        });
      });
    }
  }, [user.isSuccess, user.data]);

  if (user.isLoading) {
    return <EmptyLayout><Box className="content"><LoadingBackdrop /></Box></EmptyLayout>;
  }
  if (user.isSuccess && user.data === null) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
